/**
 * Formats an expert's full name to show first name and last initial.
 * Examples:
 *   "John Smith" → "John S."
 *   "Mary Jane Doe" → "Mary D."  (uses last word as last name)
 *   "Prince" → "Prince" (single name, returns as-is)
 *   "" → "" (empty string, returns as-is)
 *
 * @param fullName - The expert's full name (e.g., "John Smith")
 * @returns Formatted name with last initial (e.g., "John S.")
 */
export function formatExpertNameWithInitial(fullName: string): string {
  // Handle edge cases
  if (!fullName || fullName.trim() === '') {
    return fullName;
  }

  const parts = fullName.trim().split(/\s+/);

  // Single name (no spaces) - return as-is
  if (parts.length === 1) {
    return parts[0];
  }

  // Multiple parts - take first name and last initial
  const firstName = parts[0];
  const lastName = parts[parts.length - 1];
  const lastInitial = lastName[0].toUpperCase();

  return `${firstName} ${lastInitial}.`;
}

/**
 * Extracts just the first name from a full name.
 * Examples:
 *   "John Smith" → "John"
 *   "Mary Jane Doe" → "Mary"
 *   "Prince" → "Prince"
 *   "" → ""
 *
 * @param fullName - The expert's full name (e.g., "John Smith")
 * @returns Just the first name (e.g., "John")
 */
export function getFirstName(fullName: string): string {
  if (!fullName || fullName.trim() === '') {
    return fullName;
  }

  const parts = fullName.trim().split(/\s+/);
  return parts[0];
}
